/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2024 Adobe
*  All Rights Reserved.
*
* NOTICE:  All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
import { envUtil } from "dc-core";
import AcrobatJSInstanceHandler from "./ajsProvider/AcrobatJSInstanceHandler";
import { acrobatjsState } from "./ajsProvider/constants";
import { preloadSecondaryWasm, shouldEnableWasmExceptions } from "./utils/wasmUtils";
import * as constants from "./utils/constants";
import * as ajsPathUtil from "./utils/ajsPathUtils";
import { logEvent } from "./logging";
import { shouldUseWasmParts } from "./utils/utils";

class AJSHandler {
    constructor(opts, onAjsEvent) {
        this.acrobatJSInstanceHandler = new AcrobatJSInstanceHandler({
            enableAJSLogging: opts.enableAJSLogging,
            useAJSFullBuild: opts.useAJSFullBuild,
            maxActiveAJSinstanceLimit: constants.MAX_ACTIVE_AJS_INSTANCE_LIMIT,
        });
        this.options = opts;
        this._assetUriToFileHandle = {};
        this._defaultAJSHandle = null;
        this._defaultAJSHandlePromise = new Promise(resolve => { this._defaultAJSHandleResolve = resolve; });
        this._defaultAJSMapped = false;
        this._defaultInstanceAssetUri = null;
        this._ajsSetupPromise = null;
        this._reInitRequested = false;
        this._onAjsEvent = (key, ...args) =>
            onAjsEvent(key === constants.DEFAULT_AJS_INSTANCE_KEY ? this._defaultInstanceAssetUri : key, ...args);
    }

    async init() {
        if (!this._ajsSetupPromise) {
            const fileHandle = this._assetUriToFileHandle[constants.DEFAULT_AJS_INSTANCE_KEY];
            this._ajsSetupPromise = this._defaultAJSHandle || this._ajsSetup(constants.DEFAULT_AJS_INSTANCE_KEY, fileHandle);
        }
        return this._ajsSetupPromise;
    }

    _getAJSInstanceFromAssetUri(assetUri) {
        const key = assetUri;
        const fileHandle = this._assetUriToFileHandle[key];
        return this.acrobatJSInstanceHandler.getAcrobatJSInstance(fileHandle);
    }

    // Enable Progressive rendering for all users.
    // If we set useProgressive rendering explicitly false, then PR will be disabled, else it will remain enabled.
    _shouldEnableProgressiveRendering = () => (!!window.OffscreenCanvas && this.options.useProgressiveRendering);

    static _ajsGetPath() {
        return ajsPathUtil.getAjsPath();
    }

    async _ajsSetup(key, instanceHandle) {
        const ajsConfig = {
            ajsPath: AJSHandler._ajsGetPath(),
            enableProgressiveRendering: this._shouldEnableProgressiveRendering(),
            hostEnv: envUtil.getHostEnv(),
            enableWasmExceptions: shouldEnableWasmExceptions(),
            enableWasmChunking: shouldUseWasmParts(),
        };
        if (ajsConfig.enableWasmExceptions) {
            this.options.ajsExceptionSupport = "wasm";
        } else {
            this.options.ajsExceptionSupport = "js";
        }
        const fileHandlePromise = this.acrobatJSInstanceHandler.createAcrobatJSInstance({
            ajsConfig, forceEvict: true, onAjsEvent: (...args) => this._onAjsEvent(key, ...args),
        }, instanceHandle);
        if (key === constants.DEFAULT_AJS_INSTANCE_KEY && !this._defaultAJSHandle) {
            fileHandlePromise.then(this._defaultAJSHandleResolve);
            this._defaultAJSHandle = fileHandlePromise;
        }
        this._assetUriToFileHandle[key] = await fileHandlePromise;
    }

    isInstanceCached(assetUri) {
        const fileHandle = this._assetUriToFileHandle[assetUri];
        return this.acrobatJSInstanceHandler.checkInstanceState(fileHandle, acrobatjsState.CACHED);
    }

    async activateInstanceIfInactive(assetUri) {
        const fileHandle = this._assetUriToFileHandle[assetUri];
        if (this.isInstanceCached(assetUri)) {
            const { openPdfParams } = this.acrobatJSInstanceHandler.getAcrobatJSInstanceParameters(fileHandle);
            await this._ajsSetup(assetUri, fileHandle);
            await this._getAJSInstanceFromAssetUri(assetUri).openPDF(openPdfParams);
        }
    }

    async assignDefaultInstance(assetUri) {
        if (assetUri && this._defaultAJSHandle && !this._defaultAJSMapped) {
            this._assetUriToFileHandle[assetUri] = await this._defaultAJSHandle;
            this._defaultInstanceAssetUri = assetUri;
            this._defaultAJSMapped = true;
        }
    }

    /**
     *
     * This method will be used for JS related apis only, and main thread is blocked using while loop
     * just after making this call. Do not introduce any asynchronicity in this method as it will never be
     * resolved because main thread is blocked
     */
    callSync(assetUri, apiName, ...args) {
        const ajsInstance = this._getAJSInstanceFromAssetUri(assetUri || constants.DEFAULT_AJS_INSTANCE_KEY);
        if (!ajsInstance) {
            logEvent("Filehandle not present", { apiName });
            throw Error("Filehandle not present");
        }
        if (!ajsInstance[apiName]) {
            logEvent("Unknown AJS Command", { apiName });
            throw Error("Unknown AJS command");
        }
        return ajsInstance[apiName](...args);
    }

    updateDefaultAJSInstance(assetUri) {
        if (this._assetUriToFileHandle[assetUri]) {
            this._assetUriToFileHandle[constants.DEFAULT_AJS_INSTANCE_KEY] = this._assetUriToFileHandle[assetUri];
            this._defaultInstanceAssetUri = assetUri;
        } else {
            throw Error("File handle not present for assetUri:", assetUri);
        }
    }

    async callAsync(assetUri, apiName, ...args) {
        await this._defaultAJSHandlePromise;
        await this.assignDefaultInstance(assetUri);
        const fileHandle = this._assetUriToFileHandle[assetUri || constants.DEFAULT_AJS_INSTANCE_KEY];
        let mappedAssetUri = assetUri;
        if (this.acrobatJSInstanceHandler.has(fileHandle)) {
            await this.activateInstanceIfInactive(assetUri);
        } else if (assetUri && apiName !== "openPDF") {
            /* Fix for DCSV-84143 - Embed calls openPDF with dummyAssetUri but mega verbs create dcasset and call
            getPageRendition on dc assetUri. We check for single worker instance and fallback to default instance.
            This should be removed once client calls rendition provider consistently */
            logEvent(`AJS command ${apiName} called for assetUri ${assetUri} for which openPDF was not called. Using default instance.`);
            const numInstances = (new Set(Object.values(this._assetUriToFileHandle))).size;
            if (numInstances === 1) {
                mappedAssetUri = this._defaultInstanceAssetUri;
            }
        } else if (assetUri) {
            await this._ajsSetup(assetUri);
        }
        if (apiName === "openPDF") {
            this.acrobatJSInstanceHandler.setOpenPDFParameters(this._assetUriToFileHandle[assetUri], args[0]);
        }
        const ajsInstance = this._getAJSInstanceFromAssetUri(mappedAssetUri || constants.DEFAULT_AJS_INSTANCE_KEY);
        if (!ajsInstance) {
            logEvent("Filehandle not present", { apiName });
            return Promise.reject(new Error("Filehandle not present"));
        }
        if (!ajsInstance[apiName]) {
            logEvent("Unknown AJS Command", { apiName });
            return Promise.reject(new Error("Unknown AJS command"));
        }
        const ajsResponseData = await ajsInstance[apiName](...args);
        if (apiName === "openPDF") {
            // Additional wasm metadata
            ajsResponseData.enableWasmExceptions = this.options.ajsExceptionSupport === "wasm";
        }
        if (apiName === "getPageRendition") {
            preloadSecondaryWasm();
        }
        return ajsResponseData;
    }

    _resetDefaultHandleParams() {
        this._defaultAJSHandle = null;
        this._ajsSetupPromise = null;
        this._defaultAJSHandlePromise = new Promise(resolve => { this._defaultAJSHandleResolve = resolve; });
        this._defaultAJSMapped = false;
    }

    resetWorker() {
        const allFileHandlesClosed = !Object.keys(this._assetUriToFileHandle).some(key => (key !== constants.DEFAULT_AJS_INSTANCE_KEY));
        if (!allFileHandlesClosed) {
            this._reInitRequested = true;
        } else {
            this._resetDefaultHandleParams();
            this.init();
        }
    }

    close(assetUri) {
        if (assetUri in this._assetUriToFileHandle) {
            const fileHandle = this._assetUriToFileHandle[assetUri];
            this.acrobatJSInstanceHandler.erase(fileHandle);
            delete this._assetUriToFileHandle[assetUri];
        }

        const allFileHandlesClosed = !Object.keys(this._assetUriToFileHandle).some(key => (key !== constants.DEFAULT_AJS_INSTANCE_KEY));
        if (allFileHandlesClosed) {
            this._resetDefaultHandleParams();
            if (this._reInitRequested) {
                this.init();
                this._reInitRequested = false;
            }
        }
    }

    registerLicense(params) {
        const fileHandles = [...new Set(Object.values(this._assetUriToFileHandle))];
        const results = fileHandles.map(fileHandle => {
            const instance = this.acrobatJSInstanceHandler.getAcrobatJSInstance(fileHandle);
            return instance.registerLicense(params);
        });
        return Promise.all(results);
    }

    remapAssetUri(oldAssetUri, newAssetUri) {
        this._assetUriToFileHandle[newAssetUri] = this._assetUriToFileHandle[oldAssetUri];
        delete this._assetUriToFileHandle[oldAssetUri];
    }
}

export default AJSHandler;
